/**
 * §1 — the sticky section navigator (client, 2026-09-16).
 *
 * Three pills under the filter card — I Contractual, II Outcome, III Baseline (the client's
 * 2026-09-16 round-3 page) — that stick below the topbar as the reader scrolls, mark the
 * section they are currently in, and jump to any other.
 *
 * * **Real links, not buttons.** Each pill is an `<a href="#sec-…">`, so it is keyboard
 *   reachable, copy-able and works with JavaScript's smooth scroll disabled. The click
 *   handler only *upgrades* that default: `preventDefault` and `scrollIntoView`, so the
 *   section lands under the sticky bar (`scroll-margin-top` in the stylesheet) instead of
 *   beneath it, and so the URL does not grow a hash the permalink layer knows nothing about.
 * * **Reduced motion is honoured at the call site.** `behavior: 'smooth'` is a *scroll*, not
 *   a CSS animation, so a stylesheet cannot switch it off — `matchMedia` is read (guarded:
 *   jsdom has none) and the jump is instant for a reader who asked for that.
 * * **Scroll-spy, not click state.** The active pill follows the page, which is why the
 *   observer is the authority and a click only pre-sets what the observer is about to
 *   confirm. The observer is the *trigger*; the answer is read from the sections' own
 *   positions — the last one whose top has passed under the sticky bar is the one being
 *   read. Asking the observer itself ("the first section still intersecting") lagged a
 *   section behind: a section's 40 px bottom margin keeps its box inside the spy band for
 *   the first rows of the next one, so scrolling to III left the pill on II (review,
 *   2026-09-16, confirmed in the browser: III at the top of the scrollport, pill on II).
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { MouseEvent } from 'react';

import type { DashboardSectionKey } from './dashboard';

export interface SectionNavItem {
  key: DashboardSectionKey;
  numeral: string;
  label: string;
}

/**
 * The sticky bar's own height — **read from the stylesheet**, not restated here.
 *
 * `--dash-nav-h` on `.dash-view` is the one number: it is also the `scroll-margin-top` every
 * box on the page keeps clear, so a bar that changes height cannot leave the spy's band and
 * the page's scroll anchors disagreeing. The literal is only the fallback for a context with
 * no computed style to read (jsdom).
 */
const NAV_OFFSET_FALLBACK = 60;

function navOffset(): number {
  if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
    return NAV_OFFSET_FALLBACK;
  }
  const view = document.querySelector('.dash-view');
  if (!view) return NAV_OFFSET_FALLBACK;
  const raw = Number.parseFloat(
    window.getComputedStyle(view).getPropertyValue('--dash-nav-h'),
  );
  return Number.isFinite(raw) && raw > 0 ? raw : NAV_OFFSET_FALLBACK;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function SectionNav({ items }: { items: SectionNavItem[] }) {
  const { t } = useTranslation();
  const [active, setActive] = useState<DashboardSectionKey | null>(items[0]?.key ?? null);
  useEffect(() => {
    if (typeof IntersectionObserver !== 'function') return;
    /*
      Which section the reader is in, from the sections themselves: the last one whose top
      edge has passed under the sticky bar, and the first section before any of them has.
      Three `getBoundingClientRect` calls in a scroll callback is nothing, and unlike the
      observer's own answer it cannot be one section stale.
    */
    const pick = () => {
      /*
        The line to compare against is where the *bar* is, in the same coordinates
        `getBoundingClientRect` answers in: the scrollport's own top (the page scrolls inside
        `.dash-view`, which starts one topbar below the window) plus the bar's height. Using
        the bar height alone put the line 54 px too high and left every section reading as
        "not reached yet" (review, 2026-09-16).
      */
      const view = document.querySelector('.dash-view');
      const line = (view ? view.getBoundingClientRect().top : 0) + navOffset();
      let current = items[0]?.key ?? null;
      for (const item of items) {
        const el = document.getElementById(`sec-${item.key}`);
        if (el && el.getBoundingClientRect().top <= line + 4) current = item.key;
      }
      if (current) setActive(current);
    };
    /* The observer carries no answer of its own any more — it is the cue to re-read the
       sections, which is why the callback ignores its entries. */
    const io = new IntersectionObserver(pick, {
      // The band that cues a re-read: from just under the sticky bar to a little under half
      // the viewport, so the pill moves as a section's header arrives rather than as its
      // last row leaves.
      rootMargin: `-${navOffset()}px 0px -52% 0px`,
      threshold: 0,
    });
    for (const item of items) {
      const el = document.getElementById(`sec-${item.key}`);
      if (el) io.observe(el);
    }
    return () => io.disconnect();
  }, [items]);

  const jump = useCallback((event: MouseEvent, key: DashboardSectionKey) => {
    const el = document.getElementById(`sec-${key}`);
    if (!el) return;
    event.preventDefault();
    setActive(key);
    el.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  }, []);

  if (items.length === 0) return null;

  return (
    <nav
      className="dash-nav"
      role="navigation"
      aria-label={t('dashboard.nav.label')}
      data-testid="dash-nav"
    >
      <ul>
        {items.map((item) => (
          <li key={item.key}>
            <a
              href={`#sec-${item.key}`}
              className="dash-nav-pill"
              data-testid={`dash-nav-${item.key}`}
              aria-current={active === item.key ? 'true' : undefined}
              onClick={(event) => jump(event, item.key)}
            >
              <span className="dash-nav-num" aria-hidden="true">
                {item.numeral}
              </span>
              <span className="dash-nav-label">{item.label}</span>
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
